import { useId, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import { useForm, useFormState } from "react-final-form";
import { CfiItem } from "../cfiTypes";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import { CloseableDialog, Panel } from "@torqit/crew-2ls";
import { ItemSection } from "./ItemSection";

export const DEFAULT_ITEM_VALUES: CfiItem = {
  name: "",
  reason: "",
  description: "",
  professionalRecommendation: "",
  estimatedCost: "",
};

function itemHasValues(item: CfiItem): boolean {
  const hasText = (value: string | undefined) =>
    typeof value === "string" && value.trim().length > 0;

  return (
    hasText(item.name) ||
    hasText(item.reason) ||
    hasText(item.description) ||
    hasText(item.professionalRecommendation) ||
    hasText(item.estimatedCost)
  );
}

type SectionKey = "health" | "education" | "socialSupport";

interface Section {
  label: string;
  fieldName: SectionKey;
}

const sections: Section[] = [
  { label: "Health", fieldName: "health" },
  { label: "Education", fieldName: "education" },
  { label: "Social Support", fieldName: "socialSupport" },
];

interface PendingRemoval {
  category: string;
  fieldName: SectionKey;
  itemCount: number;
}

export interface ItemFieldsListProps {}

export function ItemFieldsList({}: ItemFieldsListProps) {
  const categoryId = useId();
  const form = useForm();
  const { submitFailed, errors } = useFormState({
    subscription: { submitFailed: true, errors: true },
  });

  const [enabledSections, setEnabledSections] = useState<SectionKey[]>([]);
  const showCategoryError = submitFailed && errors?.requestedItems;

  const [showRemovalWarning, setShowRemovalWarning] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(
    null,
  );

  const removeSection = (fieldName: SectionKey) => {
    setEnabledSections(
      enabledSections.filter((section) => section !== fieldName),
    );
    form.change(fieldName, undefined);
  };

  const toggleSection = (category: string, fieldName: SectionKey) => {
    if (!enabledSections.includes(fieldName)) {
      setEnabledSections([...enabledSections, fieldName]);
      form.change(fieldName, [{ ...DEFAULT_ITEM_VALUES }]);
      return;
    }

    const items =
      (form.getState().values[fieldName] as CfiItem[] | undefined) ?? [];
    if (items.some(itemHasValues)) {
      setShowRemovalWarning(true);
      setPendingRemoval({
        category,
        fieldName,
        itemCount: items.length,
      });
      return;
    }

    removeSection(fieldName);
  };

  const confirmRemoval = () => {
    if (!pendingRemoval || !showRemovalWarning) return;
    removeSection(pendingRemoval.fieldName);
    setShowRemovalWarning(false);
  };

  return (
    <Box>
      <Box pb={1}>
        <Typography id={`${categoryId}-label`} variant="h5">
          Requested Items
        </Typography>
        <Typography variant="body1">
          Select the categories that apply to your request. You can add items
          under more than one category.
        </Typography>
      </Box>
      <FormControl
        fullWidth
        error={Boolean(showCategoryError)}
        sx={{ pb: enabledSections.length > 0 ? 2 : 0 }}
      >
        <Stack
          role="group"
          aria-labelledby={`${categoryId}-label`}
          aria-describedby={
            showCategoryError ? `${categoryId}-error` : undefined
          }
          direction="row"
          gap={1}
          flexWrap="wrap"
          useFlexGap
        >
          {sections.map((section) => {
            const isEnabled = enabledSections.includes(section.fieldName);
            return (
              <Chip
                key={section.fieldName}
                label={section.label}
                onClick={() => toggleSection(section.label, section.fieldName)}
                icon={isEnabled ? <CheckIcon /> : <AddIcon />}
                color={isEnabled ? "primary" : "default"}
                variant={isEnabled ? "filled" : "outlined"}
              />
            );
          })}
        </Stack>
        {showCategoryError && (
          <FormHelperText id={`${categoryId}-error`} error>
            {errors.requestedItems}
          </FormHelperText>
        )}
      </FormControl>
      <Stack gap={2}>
        {sections.map(
          (section) =>
            enabledSections.includes(section.fieldName) && (
              <ItemSection
                key={section.fieldName}
                title={section.label}
                name={section.fieldName}
              />
            ),
        )}
      </Stack>
      <CloseableDialog
        open={showRemovalWarning}
        onClose={() => setShowRemovalWarning(false)}
      >
        <Panel title={`Remove ${pendingRemoval?.category} items`}>
          <Typography>
            You've added {pendingRemoval?.itemCount} item
            {pendingRemoval?.itemCount === 1 ? "" : "s"} under{" "}
            {pendingRemoval?.category}. Removing this category will delete them.
          </Typography>
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button onClick={() => setShowRemovalWarning(false)}>Cancel</Button>
            <Button variant="contained" onClick={confirmRemoval}>
              Remove items
            </Button>
          </Stack>
        </Panel>
      </CloseableDialog>
    </Box>
  );
}
